// components/settings/surfox-settings/sections/PhoneNumbersSection.jsx
//
// Tab-strip shell (Phase A). The original 920-line implementation moved
// verbatim to phone-numbers/ActiveNumbersTab.jsx. The shell loads eligibility
// once, decides whether to show the Port In tab at all (gated by the tenant
// flag), and routes between the two tab bodies.
//
// Phase A: Port In tab content is the eligibility coach card (or a "launching
// soon" placeholder when fully eligible). The actual port-in wizard lands in
// Phase B.

import React, { useState, useEffect } from 'react';
import { Phone, ArrowLeftRight } from 'lucide-react';
import supabase from '../../../../lib/supabaseClient';
import ActiveNumbersTab from './phone-numbers/ActiveNumbersTab';
import PortInTab from './phone-numbers/PortInTab';

export default function PhoneNumbersSection({ onUpdate }) {
  const [activeTab, setActiveTab] = useState('active');
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  useEffect(() => {
    loadEligibility();
  }, []);

  const loadEligibility = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('phone-port-in', {
        body: { action: 'eligibility-check' }
      });
      if (!error && data) {
        setEligibility(data);
      }
    } catch (e) {
      console.log('Port-in eligibility check failed (feature may not be deployed yet)');
    } finally {
      setEligibilityLoading(false);
    }
  };

  // Port In tab is hidden entirely when the tenant feature flag is off.
  // Other blockers (on_trial, no_verified_a2p, etc.) still SHOW the tab but
  // render the coach card inside — that's the motivation surface.
  const showPortInTab =
    !!eligibility &&
    !eligibility.blockers?.includes('feature_disabled');

  return (
    <div className="space-y-6">
      {showPortInTab && (
        <div className="flex space-x-1 border-b border-slate-200">
          <TabButton
            active={activeTab === 'active'}
            onClick={() => setActiveTab('active')}
            icon={Phone}
            label="Active Numbers"
          />
          <TabButton
            active={activeTab === 'port-in'}
            onClick={() => setActiveTab('port-in')}
            icon={ArrowLeftRight}
            label="Port In"
          />
        </div>
      )}

      {activeTab === 'active' && <ActiveNumbersTab onUpdate={onUpdate} />}
      {activeTab === 'port-in' && showPortInTab && (
        <PortInTab eligibility={eligibility} onUpdate={onUpdate} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-orange-500 text-slate-900'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}
